<!--
  What are you sitting down to do?

  This is `#/queue` now. It used to be a table of records with seven filter controls above it
  and two progress meters above those — meters computed over the whole corpus, so they never
  moved when the filters did. The filters have gone to the corpus browser and the meters have
  come here, where they have a denominator that means something: one task, one scope, one
  number you can finish.

  Every task is offered to everybody, with the background it calls for stated. Nobody is gated
  out of a task they are qualified for by a list somebody else maintains.
-->
<script lang="ts">
  import { untrack } from 'svelte';

  import { href, router } from '../lib/router.svelte';
  import { app } from '../lib/stores/app.svelte';
  import { annotator } from '../lib/stores/annotator.svelte';
  import { queue } from '../lib/stores/queue.svelte';
  import { taskView } from '../lib/taskViews';
  import type { TaskCard } from '../lib/api/types';
  import ProgressMeter from './ProgressMeter.svelte';

  $effect(() => {
    untrack(() => void queue.ensureCards());
  });

  function start(card: TaskCard): void {
    router.go({ name: 'queue', task: card.id });
  }

  /** A task this build has no screen for. Shown, never silently dropped — see `taskViews.ts`. */
  function unsupported(card: TaskCard): boolean {
    return taskView(card.id) === undefined;
  }

  function startable(card: TaskCard): boolean {
    return !card.blocked_reason && !unsupported(card) && annotator.ready;
  }
</script>

<div class="chooser col">
  <header class="col">
    <h2>Pick a task</h2>
    <p class="small muted">
      Each of these is a different job on the same stones, and they call for different things.
      Nothing stops you picking any of them — you know what you can read better than this page
      does.
    </p>
  </header>

  {#if app.blockers.length}
    <div class="banner error col">
      <strong>The tool is not ready to annotate</strong>
      <ul>
        {#each app.blockers as blocker, index (index)}
          <li class="small">{blocker}</li>
        {/each}
      </ul>
      <p class="small muted">
        These come straight from <span class="mono">GET /api/settings/status</span>. Run the
        commands above from <span class="mono">tools/hismaic_annotator/</span>, or set a bucket
        in <a href={href({ name: 'settings' })}>Settings</a>.
      </p>
      <div class="row">
        <button onclick={() => void app.refreshStatus()}>Re-check</button>
      </div>
    </div>
  {/if}

  {#if annotator.readOnlyAccount}
    <p class="banner warn small">
      Your account is a <strong>viewer</strong>, so you can see how far along each task is but
      cannot record anything. Ask an admin for annotator access.
    </p>
  {/if}

  {#if queue.cardsProblem}
    <div class="banner error small">
      <strong>Could not load the tasks</strong>
      <p>{queue.cardsProblem.message}</p>
      <p class="muted mono">code: {queue.cardsProblem.code}</p>
      <div class="row"><button onclick={() => void queue.loadCards()}>Retry</button></div>
    </div>
  {/if}

  {#if queue.cardsLoading && !queue.cards}
    <p class="small muted">Loading tasks…</p>
  {/if}

  <div class="cards">
    {#each queue.cards ?? [] as card (card.id)}
      <article class="panel col card">
        <div class="col" style="gap:2px">
          <h3>{card.label}</h3>
          <p class="small muted">{card.description}</p>
        </div>

        <p class="small expertise">
          <span class="tag">calls for</span>
          {card.expertise}
        </p>

        {#if card.blocked_reason}
          <!-- Never "0 of 0, nothing left": an empty scope is not a finished one. -->
          <div class="banner warn small">{card.blocked_reason}</div>
        {:else}
          <div class="col meters">
            {#each card.progress.meters as meter (meter.key)}
              <ProgressMeter
                label={meter.label}
                value={meter.value}
                total={meter.total}
                detail={meter.detail}
                tone={meter.total > 0 && meter.value >= meter.total ? 'ok' : 'accent'}
              />
            {/each}
          </div>
          <!--
            In the task's own unit. An inscription-unit task's `remaining_images` counts records
            (one look, or one fix, settles every photograph of the stone), so calling it
            "image(s) left" would name the wrong thing.
          -->
          <p class="small muted">
            {#if card.progress.unit === 'inscription'}
              {card.progress.remaining_inscriptions} inscription(s) left
            {:else}
              {card.progress.remaining_images} image(s) left across
              {card.progress.remaining_inscriptions} inscription(s)
            {/if}
            · scope is {card.scope_label} ({card.progress.inscriptions} records,
            {card.progress.images} images)
          </p>
        {/if}

        {#if unsupported(card)}
          <p class="banner warn small">
            This build has no screen for <span class="mono">{card.id}</span>. The service offers
            the task, so the frontend is older than the API it is talking to.
          </p>
        {/if}

        <div class="row">
          <button onclick={() => start(card)} disabled={!startable(card)}>
            {card.verb}
          </button>
          {#if !annotator.ready && !annotator.readOnlyAccount}
            <span class="small muted">Sign in to start.</span>
          {/if}
        </div>
      </article>
    {/each}
  </div>

  {#if queue.cards && queue.cards.length === 0}
    <p class="small muted">The service offers no tasks.</p>
  {/if}

  <p class="small muted">
    Looking for one particular stone rather than the next piece of work? That is the
    <a href={href({ name: 'browse' })}>corpus browser</a> — every record, including the ones no
    task can reach.
  </p>
</div>

<style>
  .chooser {
    padding: 12px;
    gap: 14px;
    max-width: 1280px;
    margin: 0 auto;
    width: 100%;
  }

  .cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 14px;
    align-items: start;
  }

  .card {
    gap: 12px;
  }

  .meters {
    gap: 10px;
  }

  .expertise {
    margin: 0;
  }

  .tag {
    display: inline-block;
    border: 1px solid var(--line-strong);
    border-radius: 4px;
    padding: 0 5px;
    margin-right: 4px;
    font-size: var(--text-xs);
  }

  ul {
    margin: 0;
    padding-left: 18px;
  }
</style>
